import { Component, h } from 'preact'
import axios from 'axios'
import tracker from '../../components/tracker/publish'
import SSOOverviewPresenter from './SSOOverviewPresenter'
import SAMLSetup from './SAMLSetup'
import OpenIDSetup from './OpenIDSetup'
import AlertComponent from '../ui-components/AlertComponent'
import TokenSetup from './TokenSetup'
import { Configuration, Field, FieldList, Provider, ProviderList } from './types'
import OAuth2Setup from './OAuth2Setup'
import GoogleSetup from './GoogleSetup'
import globals from '../../utils/globals'

interface Props {}

interface State {
  ssoProxyUrl: string,
  isDefaultAuthenticationEnabled: boolean,
  providers: ProviderList,
  alert: {
    visible: boolean,
    message?: string,
    type?: string | 'success' | 'error'
  },
  isLoading: boolean
}

const CONFIGURATION_URL = '/integration/sso/providers'
const BASE_PROVIDER_CONFIGURATION_URL = '/integration/sso/configuration'
const BASE_INSTALL_URL = '/integration/sso/install'
const TOGGLE_PROVIDER_URL = '/integration/sso/toggleProvider'

const PROVIDER_MAP: {[item: string]: string} = {
  saml_v2: 'saml',
  token_v2: 'token',
  openidconnect: 'openidconnect',
  oauth2: 'oauth2',
  google: 'google'
}

class SSOOverview extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      ssoProxyUrl: '',
      isDefaultAuthenticationEnabled: true,
      providers: [
        {
          name: 'saml',
          label: 'SAML 2',
          color: 'blue',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'idp_url',
              label: 'Single Sign-On URL*',
              help: 'Identity Provider Authentication URL',
              required: true
            },
            {
              name: 'idp_public_key',
              label: 'Public Key*',
              help: 'Identity Provider Public Key in X.509 format',
              required: true
            }
          ]
        },
        {
          name: 'openidconnect',
          label: 'Open ID',
          color: 'purple',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'auth_uri',
              label: 'Auth URI*',
              required: true
            },
            {
              name: 'client_id',
              label: 'Client ID*',
              required: true
            },
            {
              name: 'client_secret',
              label: 'Client Secret*',
              required: true
            },
            {
              name: 'issuer',
              label: 'Issuer*',
              required: true
            },
            {
              name: 'scope',
              label: 'Scope',
              required: false
            },
            {
              name: 'token_uri',
              label: 'Token URI*',
              required: true
            },
            {
              name: 'user_info_url',
              label: 'User Info URL',
              required: false
            }
          ]
        },
        {
          name: 'token',
          label: 'Token',
          color: 'green',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'login_url',
              label: 'Login URL*',
              required: true
            },
            {
              name: 'public_key',
              label: 'Shared or Public Key*',
              required: true
            }
          ]
        },
        {
          name: 'oauth2',
          label: 'OAuth2',
          color: 'pink',
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              type: 'text',
              name: 'auth_uri',
              label: 'Auth URI*',
              required: true
            },
            {
              type: 'text',
              name: 'client_id',
              label: 'Client ID*',
              required: true
            },
            {
              type: 'text',
              name: 'client_secret',
              label: 'Client Secret*',
              required: true
            },
            {
              type: 'text',
              name: 'issuer',
              label: 'Issuer*',
              required: true
            },
            {
              type: 'text',
              name: 'scope',
              label: 'Scope',
              required: false
            },
            {
              type: 'text',
              name: 'token_uri',
              label: 'Token URI*',
              required: true
            },
            {
              type: 'text',
              name: 'user_info_url',
              label: 'User Info URL*',
              required: true
            },
            {
              type: 'checkbox',
              name: 'pass_token_in_header',
              label: 'Pass token in Authorization Bearer',
              required: false
            }
          ]
        },
        {
          name: 'google',
          label: 'Google Sign-In',
          image: `${globals('basePath')}images/g-logo.png`,
          ...this.getDefaultProviderProperties(),
          fields: [
            {
              name: 'client_id',
              label: 'Client ID*',
              required: true
            },
            {
              name: 'client_secret',
              label: 'Client Secret*',
              required: true
            }
          ]
        }
      ],
      alert: {
        visible: false,
        message: '',
        type: ''
      },
      isLoading: true
    }

    this.onInstall = this.onInstall.bind(this)
    this.toggleProviderVisibility = this.toggleProviderVisibility.bind(this)
  }

  getDefaultProviderProperties () {
    return {
      isSetupVisible: false,
      isEnabled: false,
      isInstalled: false,
      onInstall: this.onInstall.bind(this),
      onEdit: this.onEdit.bind(this),
      configuration: {},
      errors: {}
    }
  }

  async componentDidMount (): Promise<void> {
    try {
      const response = await axios.get(CONFIGURATION_URL)
      if (response.data) {
        const { ssoProxyUrl, selfServicedSSOProvidersSettings, isDefaultAuthenticationEnabled } = response.data
        this.setState({ ssoProxyUrl, isDefaultAuthenticationEnabled })
        Object.keys(selfServicedSSOProvidersSettings).forEach((providerName: string) => {
          const isEnabled = Boolean(selfServicedSSOProvidersSettings[providerName]['isEnabled'])
          const isInstalled = Boolean(selfServicedSSOProvidersSettings[providerName]['isInstalled'])

          this.updateProvider({ name: PROVIDER_MAP[providerName] }, { isEnabled, isInstalled })
        })
      }
      this.setState({ isLoading: false })
    } catch (e) {
      this.setState({ isLoading: false })
      throw e
    }
  }

  async onInstall (provider: Provider) {
    try {
      await axios.post(BASE_INSTALL_URL, {
        provider: provider.name
      })

      this.toggleProviderSetup(provider)
    } catch (e) {
      this.setState({
        alert: {
          visible: true,
          message: `Something went wrong`,
          type: 'error'
        }
      })
      throw e
    }
  }

  onEdit (provider: Provider) {
    this.toggleProviderSetup(provider)
  }

  toggleProviderSetup (provider: Provider) {
    this.updateProvider(provider, { isSetupVisible: !provider.isSetupVisible })
  }

  async toggleProviderVisibility (provider?: Provider) {
    const { isDefaultAuthenticationEnabled } = this.state
    const enable = provider ? provider.isEnabled : isDefaultAuthenticationEnabled

    try {
      await axios.post(TOGGLE_PROVIDER_URL, {
        provider: provider?.name || 'memberlogin',
        enable: !enable
      })

      if (provider) {
        this.updateProvider(provider, { isEnabled: !enable })
      } else {
        this.setState({ isDefaultAuthenticationEnabled: !isDefaultAuthenticationEnabled })
      }
    } catch (e) {
      this.setState({
        alert: {
          visible: true,
          message: 'Something went wrong',
          type: 'error'
        }
      })
      throw e
    }
  }

  updateProvider (provider: { name: any }, property: {}) {
    const { providers } = this.state

    const index = providers.findIndex(obj => obj.name === provider.name)

    if (index < 0) return

    const updatedProviders = { ...providers[index], ...property }

    this.setState({ providers: [
      ...providers.slice(0, index),
      updatedProviders,
      ...providers.slice(index + 1)
    ] })
  }

  onInstallationSucceeded (provider: Provider) {
    const propsToUpdate = [
      { isInstalled: true },
      { errors: {} }
    ]

    propsToUpdate.forEach(prop => this.updateProvider(provider, prop))
    this.setState({
      alert: {
        visible: true,
        message: `${provider.label} is successfully installed`,
        type: 'success'
      }
    })

    const { userid, name, role } = window.inSidedData.user
    tracker.trackEvent('userevent', {
      type: 'SSO Configuration Saved',
      sso_type: provider.name,
      user_id: userid,
      user_name: name,
      user_primary_role: role
    })
  }

  async onProviderConfigurationSubmit (provider: Provider, configuration: Configuration) {
    try {
      const configurationUrl = `${BASE_PROVIDER_CONFIGURATION_URL}?provider=${provider.name}`

      await axios.post(configurationUrl, { ...configuration })

      this.onInstallationSucceeded(provider)
    } catch (e) {
      const { status, data } = e.response

      if (status === 422) {
        this.updateProvider(provider, { errors: data })
        return
      }

      this.setState({
        alert: {
          visible: true,
          type: 'error'
        }
      })
      throw e
    }
  }

  async onProviderMount (provider: Provider) {
    try {
      const configurationUrl = `${BASE_PROVIDER_CONFIGURATION_URL}?provider=${provider.name}`
      const response = await axios.get(configurationUrl)

      if (response.data) {
        const rawConfiguration = response.data
        const configuration = this.getEditableFields(rawConfiguration, provider.fields)

        this.updateProvider(provider, { configuration })
      }
    } catch (e) {
      throw e
    }
  }

  getEditableFields (configuration: Configuration, fields: FieldList) {
    const editableConfigurationFields = fields.map((field: Field) => field.name)
    return Object.keys(configuration)
      .filter(key => editableConfigurationFields.includes(key))
      .reduce((filtered: { [index: string] : any }, key: string) => {
        filtered[key] = configuration[key]
        return filtered
      }, {})
  }

  renderProviderSetup () {
    const { ssoProxyUrl, providers, isDefaultAuthenticationEnabled } = this.state

    const provider = providers.filter(provider => provider.isSetupVisible)[0]

    if (provider) {
      const commonProps = {
        provider,
        onMount: () => this.onProviderMount(provider),
        onSubmit: (configuration: Configuration) => this.onProviderConfigurationSubmit(provider, configuration),
        toggleProviderVisibility: (provider: Provider) => this.toggleProviderVisibility(provider)
      }

      if (provider.name === 'saml') {
        return <SAMLSetup ssoProxyUrl={ssoProxyUrl} {...commonProps} />
      }

      if (provider.name === 'openidconnect') {
        return <OpenIDSetup {...commonProps} />
      }

      if (provider.name === 'token') {
        return <TokenSetup {...commonProps} />
      }

      if (provider.name === 'oauth2') {
        return <OAuth2Setup {...commonProps} />
      }

      if (provider.name === 'google') {
        return <GoogleSetup {...commonProps} />
      }
    }

    return <SSOOverviewPresenter providers={providers} isDefaultAuthenticationEnabled={isDefaultAuthenticationEnabled} toggleProviderVisibility={this.toggleProviderVisibility} />
  }

  render (_: Props, { alert, isLoading }: State) {
    if (isLoading) return null
    return (
      <div>
        { alert.visible && <AlertComponent
          messageType={alert.type}
          message={alert.message}
          isVisibleFor={5000}
          sticky
          onTimeoutEnd={() => this.setState({ alert: { visible: false } })}
          onClose={() => this.setState({ alert: { visible: false } })}
        /> }
        { this.renderProviderSetup() }
      </div>
    )
  }
}

export default SSOOverview
